import React, { useEffect, useRef } from 'react';
import { Router, Route, Switch, Redirect, useHistory } from 'react-router-dom';
import { getValidatedRoutesLayout } from './index';

import loginService from '../domain/services/login.service';
import getHomePageBySource from './home.page';
import Providers from '../context/providers';
import ErrorBoundary from '../components/ErrorBoundary';
import history from '../providers/history.provider';

import PageTracking from '../components/marketing/page.tracking.component';

import { captureAndPersist } from '@quanty-labs/facilita-tracking-package'
import UploadExitGuard from '../components/upload/UploadExitGuard';
import ResumeUploadsNotice from '../components/upload/ResumeUploadsNotice';

const collectPaths = (routes) =>
    routes.reduce((paths, route) => {
        if (route.children)
            return [...paths, ...route.children.map(c => c.path)];
        if (route.path)
            return [...paths, route.path];
        return paths;
    }, []);

const renderInnerRoutes = (routes) =>
    routes.flatMap((item) => {
        const Guard = item.guard || React.Fragment;

        if (item.children) {
            return item.children.map((child) => {
                const ChildGuard = child.guard || React.Fragment;
                return (
                    <Route
                        key={child.path}
                        path={child.path}
                        exact
                        render={(props) => (
                            <ChildGuard>
                                <child.component {...props} />
                            </ChildGuard>
                        )}
                    />
                );
            });
        }

        if (item.component) {
            return (
                <Route
                    key={item.path}
                    path={item.path}
                    exact
                    render={(props) => (
                        <Guard>
                            <item.component {...props} {...(item.props || {})} />
                        </Guard>
                    )}
                />
            );
        }

        return null;
    }).filter(Boolean);

const createLayoutRoutes = () =>
    getValidatedRoutesLayout().map((layout) => {
        const Layout = layout.component;
        const allPaths = collectPaths(layout.routes);

        if (allPaths.length === 0) return null;

        return (
            <Route key={layout.name} path={allPaths} exact>
                <Layout routes={layout.routes}>
                    <Switch>
                        {renderInnerRoutes(layout.routes)}
                    </Switch>
                </Layout>
            </Route>
        );
    }).filter(Boolean);

const UnauthorizedRedirectHandler = () => {
    const history = useHistory();
    const lastUnauthorizedRedirectAt = useRef(0);

    useEffect(() => {
        const handleUnauthorized = () => {
            const now = Date.now();
            if (now - lastUnauthorizedRedirectAt.current < 500) return;
            lastUnauthorizedRedirectAt.current = now;

            if (history.location.pathname === '/login') return;
            const url = `${window.location.pathname}${window.location.search}`;
            history.replace('/login', { url });
        };

        window.addEventListener('auth:unauthorized', handleUnauthorized);
        return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
    }, [history]);

    return null;
};

const CaptureUtmOnMount = () => {
    useEffect(() => {
        captureAndPersist();
    }, []);
    return null;
};

const Routes = () => (
    <Router history={history}>
        <CaptureUtmOnMount />
        <PageTracking/>
        <UploadExitGuard />
        {loginService.getToken() && <ResumeUploadsNotice />}
        <UnauthorizedRedirectHandler />
        <Providers>
            <ErrorBoundary>
                <Switch>
                    {createLayoutRoutes()}
                    <Route
                        render={({ location }) =>
                            !loginService.getToken() ? (
                                <Redirect to={{ pathname: "/login", search: location.search }} />
                            ) : (
                                <Redirect to={{ pathname: getHomePageBySource(), search: location.search }} />
                            )
                        }
                    />
                </Switch>
            </ErrorBoundary>
        </Providers>
    </Router>
);

export default Routes;
